import {
  pageSeoQuerySchema,
  publicBannerSchema,
  publicFaqSchema,
  publicHeroSlideSchema,
  publicLocationSchema,
  publicNoticiaCardSchema,
  publicNoticiaSchema,
  publicOfertaSchema,
  publicPageSeoSchema,
  publicPartnerSchema,
  slugSchema,
  type PublicBannerList,
  type PublicFaqList,
  type PublicHeroSlideList,
  type PublicLocationList,
  type PublicNoticia,
  type PublicNoticiaCardList,
  type PublicOfertaList,
  type PublicPageSeo,
  type PublicPartnerList,
} from '@apuestapr/contracts';
import { z, type ZodError, type ZodType } from 'zod';

import { resolveApiBaseUrl } from './base-url.ts';
import { createApiClient, type ApiClient, type ApiResult } from './client.ts';

export const publicApiUrlVariable = 'VITE_PUBLIC_API_URL';

export interface PublicApiEnv {
  readonly VITE_PUBLIC_API_URL?: string | undefined;
  readonly DEV: boolean;
}

export const createPublicApiClient = (env: PublicApiEnv): ApiClient =>
  createApiClient({
    baseUrl: resolveApiBaseUrl({
      variableName: publicApiUrlVariable,
      value: env.VITE_PUBLIC_API_URL,
      isDevelopment: env.DEV,
    }),
  });

export interface AbortedApiResult {
  readonly ok: false;
  readonly aborted: true;
}

export type PublicApiResult<T> = ApiResult<T> | AbortedApiResult;
export type PublicLoader<T> = (signal?: AbortSignal) => Promise<PublicApiResult<T>>;

export const isAbortedResult = <T>(result: PublicApiResult<T>): result is AbortedApiResult =>
  'aborted' in result;

const waitForResult = <T>(
  request: Promise<ApiResult<T>>,
  signal?: AbortSignal,
): Promise<PublicApiResult<T>> => {
  if (signal === undefined) return request;
  if (signal.aborted) return Promise.resolve({ ok: false, aborted: true });

  return new Promise((resolve) => {
    const abort = (): void => {
      resolve({ ok: false, aborted: true });
    };

    signal.addEventListener('abort', abort, { once: true });
    void request.then((result) => {
      signal.removeEventListener('abort', abort);
      if (!signal.aborted) resolve(result);
    });
  });
};

const strictList = <T>(schema: ZodType<T>): ZodType<T[]> => z.array(schema);

const schemas = {
  ofertas: strictList(publicOfertaSchema.strict()),
  noticias: strictList(publicNoticiaCardSchema.strict()),
  noticia: publicNoticiaSchema.strict(),
  faqs: strictList(publicFaqSchema.strict()),
  banners: strictList(publicBannerSchema.strict()),
  heroSlides: strictList(publicHeroSlideSchema.strict()),
  partners: strictList(publicPartnerSchema.strict()),
  pageSeo: publicPageSeoSchema.strict(),
  locations: strictList(publicLocationSchema.strict()),
} as const;

export interface PublicApi {
  readonly ofertas: PublicLoader<PublicOfertaList>;
  readonly noticias: PublicLoader<PublicNoticiaCardList>;
  readonly noticia: (slug: string, signal?: AbortSignal) => Promise<PublicApiResult<PublicNoticia>>;
  readonly faqs: PublicLoader<PublicFaqList>;
  readonly banners: PublicLoader<PublicBannerList>;
  readonly heroSlides: PublicLoader<PublicHeroSlideList>;
  readonly partners: PublicLoader<PublicPartnerList>;
  readonly pageSeo: (path: string, signal?: AbortSignal) => Promise<PublicApiResult<PublicPageSeo>>;
  readonly locations: PublicLoader<PublicLocationList>;
}

const get = <T>(client: ApiClient, path: string, schema: ZodType<T>, signal?: AbortSignal) =>
  signal?.aborted === true
    ? Promise.resolve<PublicApiResult<T>>({ ok: false, aborted: true })
    : waitForResult(
        client.get(path, schema, signal === undefined ? undefined : { signal }),
        signal,
      );

const invalidParameter = <T>(error: ZodError): PublicApiResult<T> => ({
  ok: false,
  failure: {
    kind: 'malformed',
    message: 'The request parameter did not match its contract.',
    issues: error.issues.map((issue) => ({
      path: issue.path.join('.'),
      message: issue.message,
    })),
  },
});

/**
 * The public application's complete read surface. The injected transport remains credentialless,
 * each endpoint owns its strict response contract, dynamic parameters are rejected before transport,
 * and array order is returned untouched.
 */
export const createPublicApi = (client: ApiClient): PublicApi => ({
  ofertas: (signal) => get(client, '/api/content/ofertas', schemas.ofertas, signal),
  noticias: (signal) => get(client, '/api/content/noticias', schemas.noticias, signal),
  noticia: (slug, signal) => {
    const parsed = slugSchema.safeParse(slug);
    return parsed.success
      ? get(
          client,
          `/api/content/noticias/${encodeURIComponent(parsed.data)}`,
          schemas.noticia,
          signal,
        )
      : Promise.resolve(invalidParameter(parsed.error));
  },
  faqs: (signal) => get(client, '/api/content/faqs', schemas.faqs, signal),
  banners: (signal) => get(client, '/api/content/banners', schemas.banners, signal),
  heroSlides: (signal) => get(client, '/api/content/hero-slides', schemas.heroSlides, signal),
  partners: (signal) => get(client, '/api/content/partners', schemas.partners, signal),
  pageSeo: (path, signal) => {
    const parsed = pageSeoQuerySchema.safeParse({ path });
    return parsed.success
      ? get(
          client,
          `/api/content/page-seo?${new URLSearchParams(parsed.data).toString()}`,
          schemas.pageSeo,
          signal,
        )
      : Promise.resolve(invalidParameter(parsed.error));
  },
  locations: (signal) => get(client, '/api/locations', schemas.locations, signal),
});
